import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSwipeable } from 'react-swipeable';
import SignInButton from './sign-in';
import './index.css';
import LoyaltyCardPreview from './loyalty-card-preview';
import { constructEarningRule } from './utils';
import ProgressComponent from './progress-component';
import getImage from 'helpers/getImage';
import {
  BREAKPOINT_VALUES,
  LOGIN_FROM_TYPE,
  LOYALTY_VIEW_INDEX_TYPE,
  ORDER_STAGE_TYPES,
  VISIBILITY_INDEX_TYPE,
} from 'helpers/enums';
import { setState, toggleMobileCart } from 'containers/Home/actions';
import { useAuth } from 'contexts/AuthProvider';
import {
  assignPointsToUserScan,
  createLoyaltyUserSignup,
  showEnrollmentForm,
} from './actions';
import starsBackground from '../../assets/images/loyalty-presignup-slide-stars.svg';
import discountIcon from '../../assets/images/loyalty-presignup-discount.svg';
import {
  CloseButtonCWUI,
  CustomerCategoryTitleUI,
} from '@next-order/next-order-components';
import { useLoyaltyRoute } from 'helpers/hooks/useLoyaltyRoute';

const getItemPrice = (item) => {
  let itemPrice = 0;

  if (item) {
    item?.selectedSizes?.forEach((size) => {
      itemPrice = itemPrice + Number(size.price);

      item.variants?.forEach((v) => {
        itemPrice = itemPrice + Number(v.price);
      });
    });
  }

  return itemPrice;
};

export default function GuestUser({
  breakPoint,
  storeConfig,
  preSignupData,
  currency,
  storeSettings,
  themeColors,
  pointsTerminology,
  setHomeState,
  userLoyaltyDetails,
  setIsEnrollmentFormOpen,
  storeLoyaltySettings,
}) {
  const dispatch = useDispatch();
  const { storeId, restaurantId } = storeConfig;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStartX, setTouchStartX] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [dragOffset, setDragOffset] = useState(0);
  const [currentRewardCard, setCurrentRewardCard] = useState(0);
  const [isCardAnimating, setIsCardAnimating] = useState(false);
  const [autoSlideEnabled, setAutoSlideEnabled] = useState(true);
  const carouselRef = useRef(null);
  const rewardCardsRef = useRef(null);
  const { setState: setAuthState, setAuthOrderStage, setLoginFrom } = useAuth();
  const { isLoyalty, orderId } = useLoyaltyRoute();
  const { menuItems, populars, userDetails, currentUser } = useSelector(
    (state) => state.homeReducer
  );
  const { halfHalfSetup } = useSelector((state) => state.layoutReducer);

  const pointValues = [25, 40, 30];

  const containerStyle = {
    backgroundColor: themeColors.backgroundColor,
  };

  const textStyle = {
    color: themeColors.mainTextColor,
  };

  const headingColorStyle = {
    color: themeColors.labelTextColor,
  };

  const goToSlide = (slideIndex) => {
    setCurrentSlide(slideIndex);
    setDragOffset(0);
  };

  const goToPrevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? 2 : prev - 1));
    setDragOffset(0);
  };

  const goToNextSlide = () => {
    setCurrentSlide((prev) => (prev === 2 ? 0 : prev + 1));
    setDragOffset(0);
  };

  const goToNextRewardCard = () => {
    const redemptions = preSignupData?.redemptions || [];
    if (redemptions.length > 1 && !isCardAnimating) {
      setIsCardAnimating(true);
      setTimeout(() => {
        setCurrentRewardCard((prev) =>
          prev === redemptions.length - 1 ? 0 : prev + 1
        );
        setIsCardAnimating(false);
      }, 500);
    }
  };

  useEffect(() => {
    const redemptions = preSignupData?.redemptions || [];
    if (redemptions.length > 1 && currentSlide === 1) {
      const interval = setInterval(goToNextRewardCard, 2000);
      return () => clearInterval(interval);
    }
  }, [currentSlide, preSignupData?.redemptions, isCardAnimating]);

  // Auto-slide functionality
  useEffect(() => {
    let intervalId;

    if (autoSlideEnabled && !isSwiping) {
      intervalId = setInterval(() => {
        goToNextSlide();
      }, 3000);
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [currentSlide, autoSlideEnabled, isSwiping]);

  // Pause auto-sliding when user interacts with carousel
  const pauseAutoSlide = () => {
    setAutoSlideEnabled(false);

    // Resume auto-sliding after 5 seconds of inactivity
    const timeoutId = setTimeout(() => {
      setAutoSlideEnabled(true);
    }, 5000);

    return timeoutId;
  };

  const swipeHandlers = useSwipeable({
    onSwiping: (eventData) => {
      setDragOffset(eventData.deltaX);
      setIsSwiping(true);
      pauseAutoSlide();
    },
    onSwipedLeft: () => {
      goToNextSlide();
      setIsSwiping(false);
    },
    onSwipedRight: () => {
      goToPrevSlide();
      setIsSwiping(false);
    },
    onTouchEndOrOnMouseUp: () => {
      setIsSwiping(false);
      setDragOffset(0);
    },
    trackMouse: true,
    preventDefaultTouchmoveEvent: true,
  });

  const handleMouseDown = (e) => {
    setTouchStartX(e.clientX);
    setIsSwiping(true);
    pauseAutoSlide();
  };

  const handleMouseMove = (e) => {
    if (isSwiping) {
      const currentX = e.clientX;
      const diff = currentX - touchStartX;
      setDragOffset(diff);
    }
  };

  const handleMouseUp = () => {
    if (isSwiping) {
      if (dragOffset > 100) {
        goToPrevSlide();
      } else if (dragOffset < -100) {
        goToNextSlide();
      }
      setIsSwiping(false);
      setDragOffset(0);
    }
  };

  const handleMouseLeave = () => {
    if (isSwiping) {
      setIsSwiping(false);
      setDragOffset(0);
    }
  };

  const signInHandler = () => {
    dispatch(setState({ loyaltyOpen: false }));
    setAuthState({ loginFrom: LOGIN_FROM_TYPE.LOYALTY });
    if (!userDetails) {
      setAuthState({ authOrderStage: ORDER_STAGE_TYPES.OAUTH_FORM });
    }
  };

  const handleClose = () => {
    dispatch(setState({ loyaltyOpen: false }));
    dispatch(toggleMobileCart(false));
    setAuthState({ authOrderStage: ORDER_STAGE_TYPES.NONE });
  };

  const loyaltySignupHandler = async () => {
    if (userDetails && userLoyaltyDetails === null && storeLoyaltySettings) {
      const enrollmentFields =
        storeLoyaltySettings.enrollmentFields?.enrollmentFields;
      if (enrollmentFields && enrollmentFields.length > 0) {
        dispatch(showEnrollmentForm(true));
      } else {
        const { intlPhoneNumber, firstName, lastName, email, _id } =
          userDetails;
        const userEmail = email ? email : currentUser.email;
        // do loyalty signup
        const loyaltySignupResponse = await dispatch(
          createLoyaltyUserSignup({
            mobileNumber: intlPhoneNumber,
            firstName,
            lastName,
            email: userEmail,
            userId: _id,
            storeId,
            restaurantId,
          })
        );
        if (isLoyalty && orderId) {
          dispatch(
            assignPointsToUserScan({
              orderId,
              mobileNumber: intlPhoneNumber,
              restaurantId,
              userId: loyaltySignupResponse.userId,
              storeId,
            })
          );
        }
      }
    }
  };

  // Reset dragOffset when slide changes or component unmounts
  useEffect(() => {
    return () => {
      setDragOffset(0);
      setIsSwiping(false);
    };
  }, [currentSlide]);

  const getRandomItems = (items, count) => {
    if (!items || items.length === 0) return [];

    const shuffled = [...items].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  };

  const topItems = useMemo(() => {
    let items = [];
    const randomPopulars = getRandomItems(populars || [], 3);

    if (randomPopulars.length < 3 && menuItems && menuItems.length > 0) {
      const neededCount = 3 - randomPopulars.length;
      const randomMenuItems = getRandomItems(menuItems || [], neededCount);
      items = [...randomPopulars, ...randomMenuItems];
    } else {
      items = randomPopulars;
    }

    return items.slice(0, 3);
  }, [populars, menuItems]);

  const ruleStrings = useMemo(() => {
    const earningRules = preSignupData?.earnings?.slice(0, 3) || [];

    return earningRules.map((rule) => constructEarningRule(rule, currency));
  }, [preSignupData?.earnings, currency]);

  const firstSlideContent = useMemo(() => {
    return (
      <>
        <div className='slide-heading slide-1-heading'>
          <h2 style={headingColorStyle}>
            Earn {pointsTerminology} with every order
          </h2>
          <p style={textStyle}>
            {ruleStrings.length > 0 ? (
              ruleStrings.map((ruleText, index) => (
                <span key={index}>
                  {ruleText}.<br />
                </span>
              ))
            ) : (
              <span style={textStyle}>
                Earn {pointsTerminology} with every order.
              </span>
            )}
          </p>
        </div>

        <div className='loyalty-slide1-inner'>
          <div className='menu-container'>
            <div className='menu-container-heading'>Order</div>
            {topItems.length > 0 &&
              topItems.map((item, index) => {
                const itemPrice = (
                  getItemPrice(item, halfHalfSetup) || 10
                ).toFixed(2);
                const image = getImage(item.mediaPath || item.urlS3);
                return (
                  <React.Fragment key={item._id || index}>
                    <div className='menu-item'>
                      <img
                        className='menu-item-image'
                        src={image}
                        alt={item.name}
                      />
                      <div className='menu-item-text'>
                        <div className='menu-item-info'>
                          <div className='menu-item-name'>
                            {item.productName || item.name}
                          </div>
                          <div className='menu-item-price'>
                            {currency} {itemPrice}
                          </div>
                        </div>
                        <div className='menu-item-points'>
                          <div className='menu-item-points-value'>
                            {pointValues[index % pointValues.length]}{' '}
                            {pointsTerminology} ↑
                          </div>
                        </div>
                      </div>
                    </div>
                    {index < topItems.length - 1 && (
                      <hr
                        style={{
                          margin: '2px 0',
                          border: 'none',
                          height: '1px',
                          backgroundColor: '#eee',
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
          </div>
        </div>
      </>
    );
  }, [
    pointsTerminology,
    headingColorStyle,
    textStyle,
    currency,
    storeSettings,
    halfHalfSetup,
    topItems,
    ruleStrings,
  ]);

  const secondSlideContent = useMemo(() => {
    const redemptions = preSignupData?.redemptions || [];

    const nextCardIndex =
      redemptions.length > 1
        ? currentRewardCard === redemptions.length - 1
          ? 0
          : currentRewardCard + 1
        : null;

    return (
      <>
        <div className='slide-heading slide-2-heading'>
          <h2 style={headingColorStyle}>
            Redeem {pointsTerminology} for free items or discounts
          </h2>
          <p style={textStyle} className='w-full'>
            {/* Lorem ipsum dolor sit amet, ligula suspendisse nulla pretium */}
          </p>
        </div>
        <div className='loyalty-slide2-inner' ref={rewardCardsRef}>
          {redemptions.length > 0 ? (
            <div className='reward-cards-carousel'>
              <div
                className={`reward-card-stack current-card ${
                  isCardAnimating ? 'cycling-out' : ''
                }`}
              >
                <div className='rewardCard'>
                  <div
                    className='rewardCardHeader'
                    style={{ backgroundColor: themeColors.backgroundColor }}
                  >
                    <div className='rewardCardTitle'>Exclusive offer</div>
                    <div className='rewardCardIcon'>
                      <img src={discountIcon} alt='Discount' />
                    </div>
                  </div>
                  <div className='rewardCardBody'>
                    <div className='rewardCardDescription'>
                      <div className='rewardCardDescriptionTitle'>
                        {redemptions[currentRewardCard]?.name}
                      </div>
                      <div className='rewardCardDescriptionAction'>
                        <div className='rewardCardDescriptionActionButtonWrapper'>
                          <div
                            className='rewardCardDescriptionActionButton'
                            style={{
                              backgroundColor: themeColors.backgroundColor,
                            }}
                          >
                            Redeem
                          </div>
                        </div>
                        <div className='rewardCardPointsValue'>
                          {redemptions[currentRewardCard]?.points}{' '}
                          {pointsTerminology}
                        </div>
                      </div>
                    </div>
                    <div className='rewardCardImage'>
                      <img
                        src={
                          redemptions[currentRewardCard]?.imageUrl ||
                          storeSettings?.imageThemeUrl
                        }
                        alt={redemptions[currentRewardCard]?.name || 'Reward'}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {redemptions.length > 1 && nextCardIndex !== null && (
                <div
                  className={`reward-card-stack next-card ${
                    isCardAnimating ? 'cycling-in' : ''
                  }`}
                >
                  <div className='rewardCard'>
                    <div
                      className='rewardCardHeader'
                      style={{ backgroundColor: themeColors.backgroundColor }}
                    >
                      <div className='rewardCardTitle'>Exclusive offer</div>
                      <div className='rewardCardIcon'>
                        <img
                          src={discountIcon}
                          alt='Discount'
                          style={{ width: '100%', height: '100%' }}
                        />
                      </div>
                    </div>
                    <div className='rewardCardBody'>
                      <div className='rewardCardDescription'>
                        <div className='rewardCardDescriptionTitle'>
                          {redemptions[nextCardIndex]?.name}
                        </div>
                        <div className='rewardCardDescriptionAction'>
                          <div className='rewardCardDescriptionActionButtonWrapper'>
                            <div
                              className='rewardCardDescriptionActionButton'
                              style={{
                                backgroundColor: themeColors.backgroundColor,
                              }}
                            >
                              Redeem
                            </div>
                          </div>
                          <div className='rewardCardPointsValue'>
                            {redemptions[nextCardIndex]?.points}{' '}
                            {pointsTerminology}
                          </div>
                        </div>
                      </div>
                      <div className='rewardCardImage'>
                        <img
                          src={
                            redemptions[nextCardIndex]?.imageUrl ||
                            storeSettings?.imageThemeUrl
                          }
                          alt={redemptions[nextCardIndex]?.name || 'Reward'}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className='reward-card-fallback' style={textStyle}>
              No special rewards available right now.
            </div>
          )}
        </div>
      </>
    );
  }, [
    pointsTerminology,
    headingColorStyle,
    textStyle,
    storeSettings,
    preSignupData,
    currentRewardCard,
    isCardAnimating,
  ]);

  const thirdSlideContent = useMemo(
    () => (
      <>
        <div className='slide-heading slide-3-heading'>
          <h2 style={headingColorStyle}>
            Use your loyalty card in-store with Apple wallet or Online
          </h2>
          <p style={textStyle} className='w-full'>
            {/* Lorem ipsum dolor sit amet, ligula suspendisse nulla pretium. */}
          </p>
        </div>

        <div className='loyalty-benefits'>
          <div className='loyaltyPreviewCardContainer'>
            <div className='loyaltyCardPreviewBackground'>
              <div className='loyaltyCardBackgroundContent'>
                <div className='loyaltyCardName'>Loyalty</div>
                <hr className='loyaltyCardBalanceSeparator' />
                <div className='loyaltyCardBalanceBody'>
                  <div className='loyaltyCardBalance'>
                    {pointsTerminology} balance
                  </div>
                  <div className='loyaltyCardPoints'>7,600</div>
                  <ProgressComponent
                    currentPoints={7600}
                    maxPoints={10000}
                    fillColor={themeColors.backgroundColor}
                    labelTextColor={themeColors.mainTextColor}
                  />
                </div>
              </div>
            </div>
            <LoyaltyCardPreview
              mainTextColor={themeColors.mainTextColor}
              labelColor={themeColors.labelTextColor}
              bgColor={themeColors.backgroundColor}
              selectedImageTheme={storeSettings?.imageThemeUrl}
            />
          </div>
        </div>
      </>
    ),
    [
      pointsTerminology,
      headingColorStyle,
      textStyle,
      themeColors,
      storeSettings,
    ]
  );

  const getTransformValue = () => {
    const baseTransform = -(currentSlide * 100);
    const dragPercentage = carouselRef.current
      ? (dragOffset / carouselRef.current.offsetWidth) * 100
      : 0;
    return `translateX(${baseTransform + dragPercentage}%)`;
  };

  // Pause auto-sliding when clicking on dots
  const handleDotClick = (slideIndex) => {
    goToSlide(slideIndex);
    const timeoutId = pauseAutoSlide();
    return () => clearTimeout(timeoutId);
  };
  const isLoyaltyScan = isLoyalty && orderId;
  return (
    <div className='loyalty-content-wrapper'>
      <CloseButtonCWUI
        extendedClassName={`ml-5 ${
          breakPoint <= BREAKPOINT_VALUES.DESKTOP ? 'hidden' : ''
        }`}
        onClick={handleClose}
      />
      <div className='loyalty-guest-container'>
        <CustomerCategoryTitleUI extendedClassName='main-heading'>
          {isLoyaltyScan ? 'Sign up to earn reward points' : 'Get Rewarded.'}
        </CustomerCategoryTitleUI>
        <div
          className='loyalty-guest-container-inner'
          style={{
            ...containerStyle,
            '--stars-background-image': `url(${starsBackground})`,
          }}
        >
          <div
            className='loyalty-carousel'
            ref={carouselRef}
            {...swipeHandlers}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
          >
            <div
              className='loyalty-carousel-inner'
              style={{
                transform: getTransformValue(),
                transition: isSwiping ? 'none' : 'transform 0.5s ease',
              }}
            >
              <div className='loyalty-carousel-slide slide-1'>
                {firstSlideContent}
              </div>

              <div className='loyalty-carousel-slide slide-2'>
                {secondSlideContent}
              </div>

              <div className='loyalty-carousel-slide slide-3'>
                {thirdSlideContent}
              </div>
            </div>
          </div>
        </div>
        <ul className='loyalty-carousel-dots'>
          {[0, 1, 2].map((slideIndex) => (
            <li
              key={slideIndex}
              className={`loyalty-carousel-dot ${
                currentSlide === slideIndex ? 'active' : ''
              }`}
              style={{
                backgroundColor:
                  currentSlide === slideIndex
                    ? themeColors.backgroundColor
                    : '#ccc',
              }}
              onClick={() => handleDotClick(slideIndex)}
            />
          ))}
        </ul>
        <SignInButton
          userLoyaltyDetails={userLoyaltyDetails}
          userDetails={userDetails}
          onClick={signInHandler}
          onLoyaltySignUp={loyaltySignupHandler}
          themeColors={themeColors}
        />
      </div>
    </div>
  );
}
